/**
 * SupportInfo - Pantalla informativa sobre costes y cómo apoyar el proyecto
 * 501 Dart League Web
 * 
 * Explica de forma transparente los costes del proyecto, por qué es gratuito,
 * y cómo los jugadores pueden contribuir voluntariamente.
 */

import React, { useState } from 'react';
import DashboardHeader from './DashboardHeader';
import api from './api';
import paypalQR from '../../assets/paypal.jpg';
import './styles/support-info.css';
import './styles/index.css';

const SupportInfo = ({ onBack }) => {
  const [user] = useState(api.getCurrentUser());
  return (
    <div className="dart-league-dashboard training-menu-container">
      <DashboardHeader
        user={user}
        subtitle="Apoya el Proyecto"
        onBack={onBack}
      />

      <main className="support-info-main">
        {/* Intro */}
        <section className="support-intro">
          <p>
            501 Dart League es gratuita y sin anuncios. 
            Queremos que siga siendo así, pero mantener la plataforma activa tiene 
            un coste real cada mes.
          </p>
          <p>
            Nos financiamos gracias a patrocinadores — bares, tiendas y empresas 
            que apoyan el proyecto con 50€/año — y a las aportaciones voluntarias 
            de jugadores a través de PayPal.
          </p>
        </section>

        {/* Costes actuales */}
        <section className="support-costs">
          <h3>💰 ¿En qué se invierte?</h3>
          <div className="cost-grid">
            <div className="cost-item">
              <span className="cost-icon">🖥️</span>
              <div className="cost-detail">
                <span className="cost-name">Servidor Backend</span>
                <span className="cost-desc">API, WebSockets en tiempo real, procesamiento 24/7</span>
              </div>
              <span className="cost-price">~20€/mes</span>
            </div>
            <div className="cost-item">
              <span className="cost-icon">🌐</span>
              <div className="cost-detail">
                <span className="cost-name">Hosting + Dominio + SSL</span>
                <span className="cost-desc">Frontend, certificado de seguridad, CDN</span>
              </div>
              <span className="cost-price">~12€/mes</span>
            </div>
            <div className="cost-item">
              <span className="cost-icon">🗄️</span>
              <div className="cost-detail">
                <span className="cost-name">Base de Datos</span>
                <span className="cost-desc">Usuarios, partidas, ligas, backups automáticos</span>
              </div>
              <span className="cost-price">~10€/mes</span>
            </div>
            <div className="cost-item">
              <span className="cost-icon">🤖</span>
              <div className="cost-detail">
                <span className="cost-name">Herramientas de desarrollo</span>
                <span className="cost-desc">IA, testing y optimización continua</span>
              </div>
              <span className="cost-price">~20€/mes</span>
            </div>
            <div className="cost-item">
              <span className="cost-icon">💻</span>
              <div className="cost-detail">
                <span className="cost-name">Desarrollo y mantenimiento</span>
                <span className="cost-desc">Creado por <a href="https://juanmidev.es" target="_blank" rel="noopener noreferrer" style={{color: '#00ffaa'}}>juanmidev.es</a></span>
              </div>
              <span className="cost-price">❤️ Gratis</span>
            </div>
          </div>
          <p className="cost-footnote">
            Coste real: ~60€/mes. El desarrollo es voluntario y no tiene coste para el proyecto.
          </p>
        </section>

        {/* Roadmap */}
        <section className="support-future">
          <h3>🚀 Próximas funcionalidades</h3>
          <div className="cost-grid">
            <div className="cost-item">
              <span className="cost-icon">📹</span>
              <div className="cost-detail">
                <span className="cost-name">Vídeo en directo</span>
                <span className="cost-desc">Cámara integrada entre rivales (WebRTC)</span>
              </div>
              <span className="cost-price">✅ Incluido</span>
            </div>
            <div className="cost-item future">
              <span className="cost-icon">🏆</span>
              <div className="cost-detail">
                <span className="cost-name">Torneos Online</span>
                <span className="cost-desc">Brackets automáticos y rankings ELO</span>
              </div>
              <span className="cost-price">+ Servidor</span>
            </div>
            <div className="cost-item future">
              <span className="cost-icon">📱</span>
              <div className="cost-detail">
                <span className="cost-name">App para iOS</span>
                <span className="cost-desc">Android ya disponible en Google Play</span>
              </div>
              <span className="cost-price">~500-700€</span>
            </div>
            <div className="cost-item future">
              <span className="cost-icon">📊</span>
              <div className="cost-detail">
                <span className="cost-name">Estadísticas avanzadas</span>
                <span className="cost-desc">Gráficas, tendencias y comparativas</span>
              </div>
              <span className="cost-price">En desarrollo</span>
            </div>
          </div>
        </section>

        {/* Compromiso */}
        <section className="support-promise">
          <h3>🤝 Nuestro compromiso</h3>
          <div className="promise-grid">
            <div className="promise-item">
              <span className="promise-icon">🚫</span>
              <span>Sin anuncios invasivos</span>
            </div>
            <div className="promise-item">
              <span className="promise-icon">📈</span>
              <span>Mejoras y actualizaciones constantes</span>
            </div>
            <div className="promise-item">
              <span className="promise-icon">🎯</span>
              <span>Centrada en el jugador, no en el negocio</span>
            </div>
            <div className="promise-item">
              <span className="promise-icon">💪</span>
              <span>Todo se reinvierte en la plataforma</span>
            </div>
          </div>
        </section>

        {/* Ayuda */}
        <section className="support-donate">
          <h3>☕ Ayúdanos a crecer</h3>
          <p>Si disfrutas de 501 Dart League, cualquier aportación nos ayuda a seguir mejorando.</p>
          <div className="support-paypal-container">
            <a href="https://paypal.me/LiiaNnT3" target="_blank" rel="noopener noreferrer">
              <img src={paypalQR} alt="PayPal - Apoya 501 Dart League" className="support-paypal-qr" />
            </a>
            <a 
              href="https://paypal.me/LiiaNnT3" 
              target="_blank" 
              rel="noopener noreferrer"
              className="support-paypal-btn"
            >
              Enviar con PayPal
            </a>
            <p className="support-paypal-note">paypal.me/LiiaNnT3</p>
          </div>
          <p className="support-thanks">
            Gracias por ser parte de la comunidad 🎯
          </p>
        </section>

        {/* Patrocinios */}
        <section className="support-sponsor">
          <p className="sponsor-note">
            ¿Representas a una tienda de dardos o marca del sector? 
            <a href="https://paypal.me/LiiaNnT3" target="_blank" rel="noopener noreferrer"> Contacta con nosotros</a> para 
            aparecer como patrocinador en la app.
          </p>
        </section>
      </main>
    </div>
  );
};

export default SupportInfo;
